import { Component, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import {
	Label,
	LanguageService,
	StripeProduct,
	StripeService,
	StripeStudent,
} from '@services/public';
import {
	StripeCheckout,
	StripeCheckoutElementsOptions,
} from '@stripe/stripe-js';
import { APIErrors, isAPIErrors } from '@util';
import { EMAIL_PATTERN, NO_HTML_PATTERN } from 'jakapa-utilities-lite';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom } from 'rxjs';

const PAGE_PATH: string = 'pages.public.purchase.page';
const PAY_PATH: string = `${PAGE_PATH}.pay`;
const DISCOUNT_PATH: string = `${PAGE_PATH}.discount`;

const STRIPE_CHECKOUT_OPTIONS: StripeCheckoutElementsOptions = {
	appearance: {
		theme: 'stripe',
	},
};

interface FormControls {
	firstName: FormControl<string>;
	lastName: FormControl<string>;
	email: FormControl<string>;
	students: FormArray<FormGroup<FormStudentControls>>;
	promo: FormControl<string>;
	check: FormControl<boolean>;
}

interface FormStudentControls {
	firstName: FormControl<string>;
	lastName: FormControl<string>;
	email: FormControl<string>;
}

@Component({
	selector: 'app-public-purchase',
	templateUrl: './purchase.component.html',
	styleUrls: ['./purchase.component.scss'],
})
export class PublicPurchaseComponent implements OnInit {
	form: FormGroup<FormControls> = new FormGroup<FormControls>({
		firstName: new FormControl<string>(null, [
			Validators.required,
			Validators.pattern(NO_HTML_PATTERN),
			Validators.maxLength(500),
		]),
		lastName: new FormControl<string>(null, [
			Validators.required,
			Validators.pattern(NO_HTML_PATTERN),
			Validators.maxLength(500),
		]),
		email: new FormControl<string>(null, [
			Validators.required,
			Validators.pattern(EMAIL_PATTERN),
		]),
		students: new FormArray<FormGroup<FormStudentControls>>([]),
		promo: new FormControl<string>(null, [Validators.maxLength(500)]),
		check: new FormControl<boolean>(false, [Validators.requiredTrue]),
	});
	product: StripeProduct | null = null;
	loading: boolean = false;
	amount: string = '';
	private _checkout: StripeCheckout | null = null;

	// Page langauge.
	page: { [key: string]: string } = {
		studentInformation: '',
		errorFirstNameRequired: '',
		errorFirstNameLength: '',
		errorLastNameRequired: '',
		errorLastNameLength: '',
		errorEmailRequired: '',
		addStudent: '',
		removeStudent: '',
		purchaserInformation: '',
		promotionCode: '',
		paymentInformation: '',
		promoDiscount: '',
		promoError: '',
		policies: '',
		acceptPolicies: '',
		pay: '',
		alreadyHaveAnAccount: '',
		login: '',
	};
	labels: { [key in Label]?: string } = {
		[Label.EMAIL]: '',
		[Label.ERROR_EMAIL]: '',
		[Label.ERROR_NO_HTML]: '',
		[Label.FIRST_NAME]: '',
		[Label.LAST_NAME]: '',
	};

	constructor(
		private _languageSvc: LanguageService,
		private _route: ActivatedRoute,
		private _router: Router,
		private _stripeSvc: StripeService,
		private _toastSvc: ToastrService,
	) {}

	async ngOnInit(): Promise<void> {
		// Get page language.
		this._languageSvc.get([PAGE_PATH]).then((value) => {
			if (typeof value[PAGE_PATH] === 'object' && value[PAGE_PATH] !== null)
				this.page = value[PAGE_PATH];
		});
		this._languageSvc.getLabels(this.labels);

		// Get Retrieve Stripe.
		const stripe = await this._stripeSvc.stripePromise;
		if (!stripe) throw new Error('Stripe is not initialized!');

		// Get product details and Stripe client secret.
		this._route.queryParamMap.subscribe((value: ParamMap) => {
			if (!value.has('productID')) {
				this._router.navigate(['login']);
				return;
			}
			this._stripeSvc
				.getProduct(value.get('productID'))
				.subscribe(async (res: StripeProduct | APIErrors) => {
					if (!isAPIErrors(res)) {
						this.product = res;
						if (this.product.maxUsers > 1) this.addUser();
						this._checkout = await stripe.initCheckout({
							fetchClientSecret: async () => this.product?.clientSecret,
							elementsOptions: STRIPE_CHECKOUT_OPTIONS,
						});
						this._checkout.createPaymentElement().mount('#payment-element');
						this._languageSvc
							.get([PAY_PATH], {
								[PAY_PATH]: {
									amount: this._checkout.session().total.total.amount,
								},
							})
							.then((value) => {
								if (typeof value[PAY_PATH] === 'string')
									this.page.pay = value[PAY_PATH];
							});
					}
				});
		});
	}

	get f(): any {
		return this.form.controls;
	}

	get students(): FormArray<FormGroup<FormStudentControls>> {
		return this.form.controls.students;
	}

	addUser() {
		if (
			!this.product ||
			this.students.controls.length >= this.product.maxUsers - 1
		)
			return;
		this.students.push(
			new FormGroup({
				firstName: new FormControl<string>(null, [
					Validators.required,
					Validators.pattern(NO_HTML_PATTERN),
					Validators.maxLength(500),
				]),
				lastName: new FormControl<string>(null, [
					Validators.required,
					Validators.pattern(NO_HTML_PATTERN),
					Validators.maxLength(500),
				]),
				email: new FormControl<string>(null, [
					Validators.required,
					Validators.pattern(EMAIL_PATTERN),
				]),
			}),
		);
	}

	removeUser() {
		if (this.students.controls.length < 2) return;
		this.students.controls.pop();
	}

	onPromoFocus(): void {
		this.page.promoError = null;
	}

	async onPromoBlur(): Promise<void> {
		this.loading = true;
		this.page.promoError = '';
		this.page.promoDiscount = '';
		if (!this.form.value.promo) {
			await this._checkout.removePromotionCode();
		} else {
			await this._checkout.removePromotionCode();
			const result = await this._checkout.applyPromotionCode(
				this.form.value.promo,
			);
			if (result.type === 'error') this.page.promoError = result.error.message;
			else
				this._languageSvc
					.get([DISCOUNT_PATH], {
						[DISCOUNT_PATH]: {
							discount: result.session.discountAmounts[0].percentOff
								? `${result.session.discountAmounts[0].percentOff}%`
								: `$${result.session.discountAmounts[0].minorUnitsAmount / 100}`,
						},
					})
					.then((value) => {
						if (typeof value[DISCOUNT_PATH] === 'string')
							this.page.promoDiscount = value[DISCOUNT_PATH];
					});
		}
		this._languageSvc
			.get([PAY_PATH], {
				[PAY_PATH]: { amount: this._checkout.session().total.total.amount },
			})
			.then((value) => {
				if (typeof value[PAY_PATH] === 'string')
					this.page.pay = value[PAY_PATH];
			});
		this.loading = false;
	}

	async onSubmit(): Promise<void> {
		// Validate form.
		this.form.markAllAsTouched();
		if (this.form.invalid) return;

		// Update fields.
		this.loading = true;
		await this._checkout.updateEmail(this.form.value.email);
		const apiResult = await lastValueFrom(
			this._stripeSvc.updateSession(this.product.sessionID, [
				{
					firstName: this.form.value.firstName,
					lastName: this.form.value.lastName,
					email: this.form.value.email,
				},
				...(<Array<StripeStudent>>this.form.value.students),
			]),
		).catch(() => null);
		if (!apiResult || isAPIErrors(apiResult)) {
			this.loading = false;
			return;
		}

		// Confirm checkout.
		const result = await this._checkout.confirm();
		if (result.type === 'error') this._toastSvc.error(result.error.message);
		this.loading = false;
	}
}
